/**
 * FieldCombobox Component
 *
 * Searchable field combobox with type icons and optional cross-type filtering.
 * When filterDataType is set, matching fields appear first; others are dimmed below a separator.
 *
 * @module FieldCombobox
 */

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { TableFieldApiResponse } from '@loader/shared-types';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@loader/ui';

import { getTypeIcon } from '@/features/schemas/constants/fieldTypes';
import { cn } from '@/lib/utils';

interface FieldComboboxProps {
  fields: TableFieldApiResponse[];
  value: string;
  onValueChange: (value: string) => void;
  tableName: string;
  filterDataType?: string;
}

/**
 * Strip table alias prefix from field name (e.g., "Mapping.field" -> "field")
 */
export function getFieldLabel(field: TableFieldApiResponse): string {
  if (field.name.includes('.')) {
    return field.name.split('.').pop() || field.name;
  }
  return field.name;
}

export function FieldCombobox({
  fields,
  value,
  onValueChange,
  tableName,
  filterDataType,
}: FieldComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedField = fields.find((f) => f.id === value);
  const selectedLabel = selectedField ? getFieldLabel(selectedField) : null;
  const SelectedIcon = selectedField ? getTypeIcon(selectedField.data_type) : null;

  // Split fields into matching and non-matching when a filter type is set
  const matchingFields = filterDataType
    ? fields.filter((f) => f.data_type === filterDataType)
    : fields;
  const otherFields = filterDataType
    ? fields.filter((f) => f.data_type !== filterDataType)
    : [];

  const renderFieldItem = (field: TableFieldApiResponse, dimmed = false) => {
    const label = getFieldLabel(field);
    const Icon = getTypeIcon(field.data_type);
    const isSelected = value === field.id;
    return (
      <CommandItem
        key={field.id}
        value={label}
        className={dimmed ? 'opacity-50' : undefined}
        onSelect={() => {
          onValueChange(field.id);
          setOpen(false);
        }}
      >
        <Check
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            isSelected ? 'opacity-100' : 'opacity-0',
          )}
        />
        <Icon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </CommandItem>
    );
  };

  return (
    // modal={false} prevents Radix from adding scroll lock on top of the parent Dialog
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full min-w-0 justify-between rounded-none border-b bg-transparent px-1 shadow-none"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="text-muted-foreground shrink-0 text-xs">
              {tableName}.
            </span>
            {selectedField && SelectedIcon ? (
              <>
                <SelectedIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                <span className="truncate text-sm">{selectedLabel}</span>
              </>
            ) : (
              <span className="text-muted-foreground truncate text-sm">Select field</span>
            )}
          </span>
          <ChevronsUpDown className="text-muted-foreground ml-1 h-3.5 w-3.5 shrink-0" />
        </Button>
      </PopoverTrigger>
      {/* onWheel: prevent scroll from bubbling to parent Dialog's RemoveScroll */}
      <PopoverContent className="w-[220px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
        <Command>
          <CommandInput placeholder="Search fields..." />
          <CommandList>
            <CommandEmpty>No fields found.</CommandEmpty>
            {matchingFields.map((field) => renderFieldItem(field))}
            {otherFields.length > 0 && (
              <>
                <CommandSeparator />
                {otherFields.map((field) => renderFieldItem(field, true))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
